import { useMemo, type ReactNode } from 'react';
import Select, { type FormatOptionLabelMeta, type StylesConfig } from 'react-select';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (nextValue: string) => void;
  placeholder?: string;
  hasError?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  inputId?: string;
  ariaLabel?: string;
  noOptionsMessage?: string;
  onInputChange?: (nextInput: string) => void;
  disableBuiltInFilter?: boolean;
  formatOptionLabel?: (
    option: SearchableSelectOption,
    meta: FormatOptionLabelMeta<SearchableSelectOption>
  ) => ReactNode;
  menuClassName?: string;
}

const menuPortalStyles: StylesConfig<SearchableSelectOption, false> = {
  menuPortal: function raiseMenuAboveModals(base) {
    return { ...base, zIndex: 70 };
  },
};

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  hasError = false,
  isDisabled = false,
  isClearable = false,
  isLoading = false,
  inputId,
  ariaLabel,
  noOptionsMessage,
  onInputChange,
  disableBuiltInFilter = false,
  formatOptionLabel,
  menuClassName,
}: SearchableSelectProps) {
  const { t } = useTranslation('common');
  const selectedOption = useMemo(
    function findSelectedOption() {
      const match = options.find(function matches(option) {
        return option.value === value;
      });
      return match === undefined ? null : match;
    },
    [options, value]
  );

  return (
    <Select<SearchableSelectOption, false>
      unstyled
      inputId={inputId}
      aria-label={ariaLabel}
      options={options}
      value={selectedOption}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isLoading={isLoading}
      placeholder={placeholder === undefined ? '' : placeholder}
      menuPortalTarget={typeof document === 'undefined' ? undefined : document.body}
      menuPosition="fixed"
      styles={menuPortalStyles}
      filterOption={
        disableBuiltInFilter === true
          ? function keepAll() {
              return true;
            }
          : undefined
      }
      noOptionsMessage={function renderNoOptions() {
        return noOptionsMessage === undefined ? t('select.noOptions') : noOptionsMessage;
      }}
      formatOptionLabel={formatOptionLabel}
      onInputChange={function handleInputChange(nextInput, actionMeta) {
        if (onInputChange !== undefined && actionMeta.action === 'input-change') {
          onInputChange(nextInput);
        }
      }}
      onChange={function handleChange(option) {
        onChange(option === null ? '' : option.value);
      }}
      classNames={{
        control: function controlClasses(state) {
          return cn(
            'w-full min-h-[42px] px-2 bg-zoooffwhite border rounded-xl text-zooindigo transition-all cursor-pointer',
            hasError ? 'border-zooerrorborder ring-1 ring-zooerrorborder' : 'border-zooborder',
            state.isFocused && hasError === false ? 'border-zooprimary' : '',
            isDisabled ? 'opacity-60' : ''
          );
        },
        valueContainer: function valueContainerClasses() {
          return 'px-1 gap-1';
        },
        placeholder: function placeholderClasses() {
          return 'text-zoomuted';
        },
        input: function inputClasses() {
          return 'text-zooindigo';
        },
        singleValue: function singleValueClasses() {
          return 'text-zooindigo';
        },
        indicatorsContainer: function indicatorsContainerClasses() {
          return 'gap-1 pr-1';
        },
        dropdownIndicator: function dropdownIndicatorClasses() {
          return 'text-zoomuted px-1';
        },
        clearIndicator: function clearIndicatorClasses() {
          return 'text-zoomuted hover:text-zooerrortext px-1 cursor-pointer';
        },
        indicatorSeparator: function indicatorSeparatorClasses() {
          return 'hidden';
        },
        menu: function menuClasses() {
          return cn(
            'mt-2 bg-zoowhite border border-zooborder rounded-xl shadow-lg overflow-hidden',
            menuClassName
          );
        },
        menuList: function menuListClasses() {
          return 'py-1 max-h-60';
        },
        option: function optionClasses(state) {
          return cn(
            'px-4 py-2.5 text-sm cursor-pointer',
            state.isSelected
              ? 'bg-zooprimary text-zoowhite'
              : state.isFocused
                ? 'bg-zoovpalelavender text-zooindigo'
                : 'text-zooindigo'
          );
        },
        noOptionsMessage: function noOptionsMessageClasses() {
          return 'px-4 py-3 text-sm text-zoomuted';
        },
      }}
    />
  );
}
